// @ts-nocheck
import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { REACTION_TYPES } from '@/components/social-wall/ReactionPicker';
import { parseMentions, saveMentions } from '@/hooks/useMentionSearch';
import { parseHashtags, saveHashtags } from '@/hooks/useHashtagSearch';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const PAGE_SIZE = 10;

export type FeedFilter = 'all' | 'following' | 'trending';

export type ReactionFilter = 'trophy' | 'grind' | 'brain' | 'clutch' | 'fire_today' | null;

// Map filter IDs to reaction type keys used in reactions_count JSONB
const getReactionTypeKey = (filter: ReactionFilter): string | null => {
  switch (filter) {
    case 'trophy':
      return 'trophy';
    case 'grind':
      return 'grind';
    case 'brain':
      return 'brain';
    case 'clutch':
      return 'clutch';
    case 'fire_today':
      return 'fire';
    default:
      return null;
  }
};

export interface LinkPreviewType {
  title?: string | null;
  description?: string | null;
  image?: string | null;
  domain?: string;
  isYouTube?: boolean;
  videoId?: string | null;
}

export interface WallPost {
  id: string;
  profile_id: string;
  content: string | null;
  media_urls: string[] | null;
  link_url: string | null;
  link_preview: LinkPreviewType | null;
  post_type: 'text' | 'media' | 'link' | 'stats' | 'training';
  stats_data: Record<string, any> | null;
  template_id: string | null;
  template_data: Record<string, string> | null;
  likes_count: number;
  reactions_count: Record<string, number>;
  comments_count: number;
  share_count?: number;
  is_flagged: boolean;
  is_hidden: boolean;
  is_trending?: boolean;
  trending_at?: string | null;
  is_boosted?: boolean;
  boosted_at?: string | null;
  boosted_until?: string | null;
  is_pinned?: boolean;
  pinned_at?: string | null;
  created_at: string;
  updated_at: string;
  profiles: {
    id: string;
    name: string | null;
    avatar_url: string | null;
    username: string | null;
  };
  user_reactions?: string[];
}

export interface CreatePostData {
  content: string;
  media_urls?: string[];
  link_url?: string;
  link_preview?: LinkPreviewType;
  post_type: 'text' | 'media' | 'link' | 'stats';
  stats_data?: Record<string, any>;
  template_id?: string;
  template_data?: Record<string, string>;
}

export const useSocialWall = (
  profileId?: string, 
  filter: FeedFilter = 'all',
  reactionFilter: ReactionFilter = null
) => {
  const queryClient = useQueryClient();
  const [posts, setPosts] = useState<WallPost[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [followingCount, setFollowingCount] = useState<number | null>(null);
  const isLoadingMoreRef = useRef(false);
  const cursorRef = useRef<string | null>(null);

  // Fetch posts with pagination
  const fetchPosts = async (cursor?: string | null): Promise<WallPost[]> => {
    // If reaction filter is active, use a different query approach
    if (reactionFilter) {
      return fetchPostsByReaction(cursor);
    }

    // If filtering by following, first get the list of followed profile IDs
    let followedIds: string[] = [];
    
    if (filter === 'following' && profileId) {
      const { data: followsData, error: followsError } = await supabase
        .from('profile_follows')
        .select('followed_id')
        .eq('follower_id', profileId);
      
      if (followsError) throw followsError;
      
      followedIds = followsData?.map(f => f.followed_id) || [];
      setFollowingCount(followedIds.length);
      
      // If user follows nobody, return empty array
      if (followedIds.length === 0) {
        return [];
      }
    }

    let query = supabase
      .from('wall_posts')
      .select(`
        *,
        profiles:profile_id (
          id,
          name,
          avatar_url,
          username
        )
      `)
      .eq('is_hidden', false);

    // Apply filter-specific ordering and conditions
    if (filter === 'trending') {
      query = query
        .eq('is_trending', true)
        .order('trending_at', { ascending: false })
        .limit(PAGE_SIZE);
    } else {
      query = query
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);
    }

    // Filter by followed users if in 'following' mode
    if (filter === 'following' && followedIds.length > 0) {
      query = query.in('profile_id', followedIds);
    }

    if (cursor) {
      query = query.lt('created_at', cursor);
    }

    const { data: postsData, error: postsError } = await query;

    if (postsError) throw postsError;

    return addUserReactions(postsData);
  };

  // Fetch posts filtered by reaction type
  const fetchPostsByReaction = async (cursor?: string | null): Promise<WallPost[]> => {
    const reactionKey = getReactionTypeKey(reactionFilter);
    if (!reactionKey) return [];

    let query = supabase
      .from('wall_posts')
      .select(`
        *,
        profiles:profile_id (
          id,
          name,
          avatar_url,
          username
        )
      `)
      .eq('is_hidden', false);

    // For "Today's 🔥", filter by today's date
    if (reactionFilter === 'fire_today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      query = query.gte('created_at', today.toISOString());
    }

    // Limit results
    query = query.limit(50); // Fetch more to sort client-side

    const { data: postsData, error: postsError } = await query;

    if (postsError) throw postsError;

    // Sort by reaction count client-side (JSONB sorting is complex in Supabase)
    const sortedPosts = (postsData || [])
      .filter(post => {
        const count = (post.reactions_count as Record<string, number>)?.[reactionKey] || 0;
        return count > 0;
      })
      .sort((a, b) => {
        const countA = (a.reactions_count as Record<string, number>)?.[reactionKey] || 0;
        const countB = (b.reactions_count as Record<string, number>)?.[reactionKey] || 0;
        return countB - countA;
      })
      .slice(0, PAGE_SIZE);

    return addUserReactions(sortedPosts);
  };

  // Add user reactions to posts
  const addUserReactions = async (postsData: any[]): Promise<WallPost[]> => {
    if (profileId && postsData.length > 0) {
      const postIds = postsData.map(p => p.id);
      const { data: reactionsData } = await supabase
        .from('wall_post_reactions')
        .select('post_id, reaction_type')
        .eq('profile_id', profileId)
        .in('post_id', postIds);

      // Group reactions by post_id
      const reactionsByPost: Record<string, string[]> = {};
      reactionsData?.forEach(r => {
        if (!reactionsByPost[r.post_id]) {
          reactionsByPost[r.post_id] = [];
        }
        reactionsByPost[r.post_id].push(r.reaction_type);
      });
      
      return postsData.map(post => ({
        ...post,
        reactions_count: post.reactions_count || {},
        user_reactions: reactionsByPost[post.id] || []
      })) as WallPost[];
    }

    return postsData.map(post => ({
      ...post,
      reactions_count: post.reactions_count || {},
      user_reactions: []
    })) as WallPost[];
  };

  // Initial load - refetch when filter changes
  const {
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['wall_posts', filter, reactionFilter, profileId],
    queryFn: async () => {
      cursorRef.current = null; // Reset cursor on filter change
      const initialPosts = await fetchPosts(null);
      setPosts(initialPosts);
      // Disable pagination for reaction-filtered posts (they're sorted client-side)
      setHasMore(reactionFilter ? false : initialPosts.length === PAGE_SIZE);
      cursorRef.current = initialPosts.length > 0 
        ? initialPosts[initialPosts.length - 1].created_at 
        : null;
      return initialPosts;
    },
    enabled: true,
  });

  // Load more posts
  const loadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMore) return;
    
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    
    try {
      const morePosts = await fetchPosts(cursorRef.current);
      
      if (morePosts.length < PAGE_SIZE) {
        setHasMore(false);
      }
      
      if (morePosts.length > 0) {
        cursorRef.current = morePosts[morePosts.length - 1].created_at;
        setPosts(prev => [...prev, ...morePosts]);
      }
    } catch (err) {
      console.error('Error loading more posts:', err);
      toast.error('Failed to load more posts');
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [hasMore, profileId]);

  // Create a new post
  const createPostMutation = useMutation({
    mutationFn: async (data: CreatePostData) => {
      if (!profileId) throw new Error('You must be logged in to post');

      const { data: newPost, error } = await supabase
        .from('wall_posts')
        .insert({
          profile_id: profileId,
          content: data.content,
          media_urls: data.media_urls || null,
          link_url: data.link_url || null,
          link_preview: data.link_preview || null,
          post_type: data.post_type,
          stats_data: data.stats_data || null,
          template_id: data.template_id || null,
          template_data: data.template_data || null,
        })
        .select(`
          *,
          profiles:profile_id (
            id,
            name,
            avatar_url,
            username
          )
        `)
        .single();

      if (error) throw error;
      return newPost as WallPost;
    },
    onSuccess: async (newPost, variables) => {
      // Prepend new post to the list
      setPosts(prev => [{ ...newPost, user_reactions: [], reactions_count: {} }, ...prev]);
      toast.success('Post created successfully!');
      
      // Save mentions
      if (profileId && variables.content) {
        const mentionedUsernames = parseMentions(variables.content);
        if (mentionedUsernames.length > 0) {
          await saveMentions(mentionedUsernames, profileId, newPost.id);
        }
        
        // Save hashtags
        const hashtags = parseHashtags(variables.content);
        if (hashtags.length > 0) {
          await saveHashtags(hashtags, newPost.id);
        }
      }
      
      // Update "posting" streak
      if (profileId) {
        await supabase.rpc('update_user_streak', { 
          p_profile_id: profileId, 
          p_streak_type: 'posting' 
        });
      }
      
      // Trigger AI moderation asynchronously (don't block the user)
      if (variables.content && profileId) {
        invokeEdgeFunction('moderate-content', {
          body: {
            content: variables.content,
            content_type: 'post',
            content_id: newPost.id,
            author_id: profileId,
          },
        }).catch(err => {
          console.error('Moderation error:', err);
        });
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create post');
    },
  });

  // Toggle reaction on a post
  const toggleReactionMutation = useMutation({
    mutationFn: async ({ 
      postId, 
      reactionType, 
      hasReaction, 
      postAuthorId 
    }: { 
      postId: string; 
      reactionType: string; 
      hasReaction: boolean; 
      postAuthorId: string;
    }) => {
      if (!profileId) throw new Error('You must be logged in to react');

      if (hasReaction) {
        // Remove reaction
        const { error: deleteError } = await supabase
          .from('wall_post_reactions')
          .delete()
          .eq('post_id', postId)
          .eq('profile_id', profileId)
          .eq('reaction_type', reactionType);

        if (deleteError) throw deleteError;
      } else {
        // Add reaction
        const { error: insertError } = await supabase
          .from('wall_post_reactions')
          .insert({
            post_id: postId,
            profile_id: profileId,
            reaction_type: reactionType,
          });

        if (insertError) throw insertError;

        // Create notification for post author (unless reacting to own post)
        if (postAuthorId && postAuthorId !== profileId) {
          const reactionEmoji = REACTION_TYPES.find(r => r.id === reactionType)?.emoji || '🔥';
          await supabase
            .from('wall_notifications')
            .insert({
              recipient_id: postAuthorId,
              actor_id: profileId,
              post_id: postId,
              type: 'like',
              // Store reaction type in metadata if needed
            });
        }
      }

      // Update reactions_count on the post
      const { data: allReactions } = await supabase
        .from('wall_post_reactions')
        .select('reaction_type')
        .eq('post_id', postId);

      const newCounts: Record<string, number> = {};
      allReactions?.forEach(r => {
        newCounts[r.reaction_type] = (newCounts[r.reaction_type] || 0) + 1;
      });

      await supabase
        .from('wall_posts')
        .update({ reactions_count: newCounts })
        .eq('id', postId);

      return { postId, reactionType, wasAdded: !hasReaction, newCounts, postAuthorId };
    },
    onSuccess: async ({ postId, reactionType, wasAdded, newCounts, postAuthorId }) => {
      // Update local state
      setPosts(prev => prev.map(post => {
        if (post.id !== postId) return post;
        
        const userReactions = wasAdded
          ? [...(post.user_reactions || []), reactionType]
          : (post.user_reactions || []).filter(r => r !== reactionType);
        
        return {
          ...post,
          user_reactions: userReactions,
          reactions_count: newCounts,
        };
      }));

      // Check and award badges for both reactor and post author
      if (wasAdded) {
        // Update "giving" streak for the reactor
        if (profileId) {
          supabase.rpc('update_user_streak', { 
            p_profile_id: profileId, 
            p_streak_type: 'giving' 
          });
          
          // Check badges for the reactor (reactions_given badges)
          const { data: badgeResult } = await supabase.rpc('check_and_award_badges', { p_profile_id: profileId });
          if (badgeResult?.badges_awarded?.length > 0) {
            // Fetch badge details to show in toast
            const { data: badgeDetails } = await supabase
              .from('wall_badges')
              .select('emoji, name')
              .in('id', badgeResult.badges_awarded);
            
            badgeDetails?.forEach((badge: { emoji: string; name: string }) => {
              toast.success(`You earned the ${badge.emoji} ${badge.name} badge!`, {
                duration: 5000,
              });
            });
          }
        }
        // Update "receiving" streak for the post author and check badges
        if (postAuthorId && postAuthorId !== profileId) {
          supabase.rpc('update_user_streak', { 
            p_profile_id: postAuthorId, 
            p_streak_type: 'receiving' 
          });
          supabase.rpc('check_and_award_badges', { p_profile_id: postAuthorId });
        }
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update reaction');
    },
  });

  // Report a post
  const reportPostMutation = useMutation({
    mutationFn: async ({ postId, reason, details }: { postId: string; reason: string; details?: string }) => {
      if (!profileId) throw new Error('You must be logged in to report posts');

      const { error } = await supabase
        .from('wall_post_reports')
        .insert({
          post_id: postId,
          reported_by: profileId,
          reason,
          details: details || null,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Post reported. Thank you for helping keep our community safe.');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to report post');
    },
  });

  return {
    posts,
    isLoading,
    error,
    refetch,
    hasMore,
    isLoadingMore,
    loadMore,
    followingCount,
    createPost: createPostMutation.mutate,
    isCreating: createPostMutation.isPending,
    toggleReaction: toggleReactionMutation.mutate,
    isTogglingReaction: toggleReactionMutation.isPending,
    reportPost: reportPostMutation.mutate,
    isReporting: reportPostMutation.isPending,
  };
};
